/**
 * 0zzz-uhy (CEO decisions Q34a/b, 2026-10-13): a user in NO hierarchy who opens /safeword or triggers a safeword has
 * nobody in the app to tell. They get an honest dead end (this copy plus crisis resources), and ONE call tells the
 * platform admin (public id and timestamp only, server side).
 *
 * Copy rule (same as the 2026-08-09 CRITICAL fix): state only what is certain. "No one in the app was told" is
 * certain. Whether the platform team sees or answers the alert is NOT, so the copy never promises it.
 */
import { safewordAPI } from '../services/api';

export const NO_HIERARCHY_HEADLINE = 'You are not in a hierarchy';
export const NO_HIERARCHY_TOLD_NO_ONE =
  'No one in the app was told. Safeword alerts go to the partner, Dominant or keyholder in your hierarchy, and you do not have one right now.';
export const NO_HIERARCHY_PLATFORM_NOTE =
  'Helm may let its platform team know that a safeword was used on your account. It sends your account reference only. Do not wait for a reply. If you need help now, use the resources below.';

export const ORG_LIST_FAILED_HEADLINE = 'We could not load your hierarchies';
export const ORG_LIST_FAILED_BODY =
  'Your safeword buttons need your hierarchies, and they did not load. Reload this page to try again. If you need help now, use the resources below.';

export const NO_HIERARCHY_BUTTON_LABEL = 'Tell the Helm team';
export const NO_HIERARCHY_SENT =
  'Your request was sent to the Helm team with your account reference only. Do not wait for a reply. If you need help now, use the resources below.';
export const NO_HIERARCHY_SEND_FAILED = 'That could not be sent. You can try again. If you need help now, use the resources below.';
export const NO_HIERARCHY_ALREADY_SENT =
  'Your request was already sent a short while ago, so it cannot be sent again yet. Please wait. If you need help now, use the resources below.';
export const HIERARCHY_UNCONFIRMED = 'We could not confirm your hierarchy. Please try again.';

export type NoHierarchyReportOutcome = 'sent' | 'member' | 'rate_limited' | 'failed';

/**
 * Sends the one platform alert, on an explicit press only (CEO 2026-10-13). NEVER rejects. What the user sees never
 * depends on it, with ONE exception: a 409
 * ('member') means the server found a hierarchy for this user, so the empty organization list on screen was a failed
 * load, not the truth. The page then drops the "you are not in a hierarchy" claim. A rate limit (429), a network
 * error and any other failure change nothing ('failed').
 */
export function reportNoHierarchySafeword(): Promise<NoHierarchyReportOutcome> {
  return Promise.resolve()
    .then(() => safewordAPI.reportNoHierarchy())
    .then((): NoHierarchyReportOutcome => 'sent')
    .catch((err: unknown): NoHierarchyReportOutcome => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      return status === 409 ? 'member' : status === 429 ? 'rate_limited' : 'failed';
    });
}
